import React, { useEffect, useState } from "react";
import { Row, Col, Card, Table, Button, Input, Modal, Typography, Tag, Space, Drawer, Form, Select, notification } from "antd";
import { DeleteOutlined, EditOutlined, ExclamationCircleOutlined, PlusOutlined, SearchOutlined, ReloadOutlined } from "@ant-design/icons";
import axios from "axios";
import { baseUrl } from "../../config";

const { confirm } = Modal;
const { Item } = Form;
const { Option } = Select;

function PopDistrictsList() {
  const [form] = Form.useForm();
  const [dataList, setDataList]     = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit]     = useState(15);
  const [search, setSearch]         = useState('');
  const [loading, setLoading]       = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editId, setEditId]         = useState(null);
  const [saving, setSaving]         = useState(false);

  // Filter state
  const [countries, setCountries]         = useState([]);
  const [filterStates, setFilterStates]   = useState([]);
  const [filterCountry, setFilterCountry] = useState('');
  const [filterState, setFilterState]     = useState('');

  // Drawer cascading
  const [drawerStates, setDrawerStates] = useState([]);

  const authHeader = { Authorization: `Bearer ${localStorage.getItem('accessToken')}` };

  useEffect(() => {
    axios.get(`${baseUrl}/pop-country/list-all`, { headers: authHeader })
      .then(r => { if (r.data.status) setCountries(r.data.result); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchList = (page = currentPage, q = search, countryId = filterCountry, stateId = filterState) => {
    setLoading(true);
    const params = { page, limit, search: q };
    if (countryId) params.country_id = countryId;
    if (stateId)   params.state_id   = stateId;
    axios.get(`${baseUrl}/pop-districts`, { headers: authHeader, params })
      .then(r => {
        if (r.data.status) {
          setDataList(r.data.result.map((item, i) => ({ ...item, key: (page - 1) * limit + i + 1 })));
          setTotalCount(r.data.totalCount);
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchList(); }, [currentPage]); // eslint-disable-line

  const onFilterCountryChange = (v) => {
    setFilterCountry(v || '');
    setFilterState('');
    setFilterStates([]);
    setCurrentPage(1);
    fetchList(1, search, v || '', '');
    if (v) {
      axios.get(`${baseUrl}/pop-states/list-all`, { headers: authHeader, params: { country_id: v } })
        .then(r => { if (r.data.status) setFilterStates(r.data.result); });
    }
  };

  const onFilterStateChange = (v) => {
    setFilterState(v || '');
    setCurrentPage(1);
    fetchList(1, search, filterCountry, v || '');
  };

  const handleReset = () => {
    setFilterCountry(''); setFilterState(''); setFilterStates([]);
    setSearch(''); setCurrentPage(1);
    fetchList(1, '', '', '');
  };

  // Drawer helpers
  const onDrawerCountryChange = (v) => {
    form.setFieldsValue({ state_id: undefined });
    setDrawerStates([]);
    if (v) {
      axios.get(`${baseUrl}/pop-states/list-all`, { headers: authHeader, params: { country_id: v } })
        .then(r => { if (r.data.status) setDrawerStates(r.data.result); });
    }
  };

  const openAdd = () => {
    form.resetFields(); form.setFieldsValue({ status: '1' });
    setDrawerStates([]); setEditId(null); setDrawerOpen(true);
  };

  const openEdit = async (r) => {
    setDrawerStates([]); setEditId(r.id); setDrawerOpen(true);
    if (r.country_id) {
      const res = await axios.get(`${baseUrl}/pop-states/list-all`, { headers: authHeader, params: { country_id: r.country_id } });
      if (res.data.status) setDrawerStates(res.data.result);
    }
    form.setFieldsValue({
      country_id: r.country_id ? parseInt(r.country_id) : undefined,
      state_id:   r.state_id   ? parseInt(r.state_id)   : undefined,
      name: r.name, status: String(r.status),
    });
  };

  const onSave = async (values) => {
    setSaving(true);
    try {
      if (editId) await axios.put(`${baseUrl}/pop-districts/${editId}`, values, { headers: authHeader });
      else        await axios.post(`${baseUrl}/pop-districts`, values, { headers: authHeader });
      notification.success({ message: editId ? 'District updated' : 'District added' });
      setDrawerOpen(false); fetchList();
    } catch (e) {
      notification.error({ message: e.response?.data?.message || 'Error' });
    } finally { setSaving(false); }
  };

  const handleDelete = (id) => confirm({
    title: 'Delete this district?', icon: <ExclamationCircleOutlined />,
    okText: 'Yes, Delete', okType: 'danger', cancelText: 'Cancel',
    onOk: async () => {
      await axios.delete(`${baseUrl}/pop-districts/${id}`, { headers: authHeader });
      notification.success({ message: 'Deleted' }); fetchList();
    },
  });

  const columns = [
    { title: '#', dataIndex: 'key', key: 'key', width: 60 },
    { title: 'District Name', dataIndex: 'name', key: 'name' },
    { title: 'State', dataIndex: 'state_name', key: 'state_name' },
    { title: 'Country', dataIndex: 'country_name', key: 'country_name' },
    { title: 'Status', dataIndex: 'status', key: 'status', width: 90,
      render: s => <Tag color={String(s) === '1' ? 'green' : 'red'}>{String(s) === '1' ? 'Active' : 'Inactive'}</Tag> },
    { title: 'Actions', key: 'actions', fixed: 'right', width: 140,
      render: (_, record) => (
        <Space size={4}>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)}
            style={{ background: '#7a0000', borderColor: '#7a0000', color: '#fff' }}>Edit</Button>
          <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record.id)}>Delete</Button>
        </Space>
      ),
    },
  ];

  return (
    <div className="tabled">
      <Row gutter={[24, 0]}>
        <Col span={24}>
          <Card
            title={<span style={{ color: '#1e293b', fontWeight: 700, fontSize: 15 }}>Districts <Tag color="purple">{totalCount}</Tag></span>}
            headStyle={{ background: '#f8fafc', borderBottom: '1.5px solid #e2e8f0' }}
            extra={
              <Button icon={<PlusOutlined />} onClick={openAdd}
                style={{ background: '#7a0000', borderColor: '#7a0000', color: '#fff', fontWeight: 600 }}>
                Add District
              </Button>
            }
          >
            {/* ── Filter Bar ── */}
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '12px 16px', marginBottom: 16 }}>
              <Space wrap size={10}>
                <Select placeholder="Country" style={{ width: 160 }} allowClear showSearch optionFilterProp="children"
                  value={filterCountry || undefined} onChange={onFilterCountryChange}>
                  {countries.map(c => <Option key={c.id} value={String(c.id)}>{c.name}</Option>)}
                </Select>

                <Select placeholder="State" style={{ width: 180 }} allowClear showSearch optionFilterProp="children"
                  value={filterState || undefined} onChange={onFilterStateChange}
                  disabled={!filterCountry && filterStates.length === 0}>
                  {filterStates.map(s => <Option key={s.id} value={String(s.id)}>{s.name}</Option>)}
                </Select>

                <Input.Search
                  placeholder="Search district name..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  onSearch={val => { setSearch(val); setCurrentPage(1); fetchList(1, val, filterCountry, filterState); }}
                  allowClear
                  style={{ width: 240 }}
                  enterButton={<Button icon={<SearchOutlined />} style={{ background: '#7a0000', borderColor: '#7a0000', color: '#fff' }}>Search</Button>}
                />

                <Button icon={<ReloadOutlined />} onClick={handleReset}>Reset</Button>
              </Space>
            </div>

            <Table
              columns={columns} dataSource={dataList} loading={loading} size="small"
              scroll={{ x: 'max-content' }}
              pagination={{ current: currentPage, pageSize: limit, total: totalCount, onChange: p => setCurrentPage(p), showTotal: t => `Total ${t} districts`, showSizeChanger: false }}
            />
          </Card>
        </Col>
      </Row>

      <Drawer
        title={<span style={{ fontWeight: 700, color: '#1e293b' }}>{editId ? 'Edit District' : 'Add District'}</span>}
        width={500} open={drawerOpen} onClose={() => setDrawerOpen(false)} bodyStyle={{ paddingBottom: 80 }}
        footer={
          <Space>
            <Button loading={saving} onClick={() => form.submit()}
              style={{ background: '#7a0000', borderColor: '#7a0000', color: '#fff' }}>Save</Button>
            <Button onClick={() => setDrawerOpen(false)}>Cancel</Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical" onFinish={onSave}>
          <Item name="country_id" label="Country" rules={[{ required: true, message: 'Required' }]}>
            <Select placeholder="Select Country" showSearch optionFilterProp="children" onChange={onDrawerCountryChange} style={{ width: '100%' }}>
              {countries.map(c => <Option key={c.id} value={c.id}>{c.name}</Option>)}
            </Select>
          </Item>
          <Item name="state_id" label="State" rules={[{ required: true, message: 'Required' }]}>
            <Select placeholder="Select State" showSearch optionFilterProp="children" disabled={drawerStates.length === 0} style={{ width: '100%' }}>
              {drawerStates.map(s => <Option key={s.id} value={s.id}>{s.name}</Option>)}
            </Select>
          </Item>
          <Item name="name" label="District Name" rules={[{ required: true, message: 'Required' }]}>
            <Input placeholder="Enter district name" />
          </Item>
          <Item name="status" label="Status">
            <Select><Option value="1">Active</Option><Option value="0">Inactive</Option></Select>
          </Item>
        </Form>
      </Drawer>
    </div>
  );
}

export default PopDistrictsList;
